/**
 * PCW Evaluations Tab Component for HR Payroll Page
 * Displays Personal Care Worker evaluations with duty ratings
 */
import { Button } from "@/components/ui/button";
import { FileText, Plus, Send, Clock, Eye, CheckCircle } from "lucide-react";

export function PCWEvaluationsTab({
  pcwEvaluations,
  pcwGradingScale,
  pcwDuties,
  onAddClick,
  onExportPdf,
  onCompleteAndSend,
  onResend,
  getGradeColor,
  getGradeLabel
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="pcw-evaluations-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">PCW Evaluations</h2>
          <p className="text-sm text-slate-500">Evaluate Personal Care Workers based on their duties</p>
        </div>
        <Button onClick={onAddClick} className="gap-2" data-testid="add-pcw-evaluation-btn">
          <Plus className="w-4 h-4" />
          New PCW Evaluation
        </Button>
      </div>

      {/* Grading Scale Reference */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg">
        <h4 className="text-sm font-medium text-slate-700 mb-2">Grading Scale</h4>
        <div className="flex flex-wrap gap-2">
          {pcwGradingScale.map((grade) => (
            <span key={grade.value} className={`px-3 py-1 rounded-full text-xs font-medium ${getGradeColor(grade.value)}`}>
              {grade.value} = {grade.label}
            </span>
          ))}
        </div>
      </div>

      {pcwEvaluations.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No PCW evaluations yet</p>
          <p className="text-sm">Create your first evaluation using the button above</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pcwEvaluations.map((eval_) => (
            <div key={eval_.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">{eval_.employee_name}</h3>
                  <p className="text-sm text-slate-500">
                    Evaluated by {eval_.evaluator_name} on {new Date(eval_.evaluation_date).toLocaleDateString()}
                  </p>
                  {eval_.period_start && eval_.period_end && (
                    <p className="text-xs text-slate-400">
                      Period: {eval_.period_start} to {eval_.period_end}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(Math.round(eval_.average_rating))}`}>
                    Avg: {eval_.average_rating?.toFixed(1)} ({getGradeLabel(Math.round(eval_.average_rating))})
                  </span>
                  <p className={`text-xs mt-1 ${
                    eval_.status === 'completed' || eval_.status === 'acknowledged' ? 'text-emerald-600' : 
                    eval_.status === 'pending_signature' ? 'text-amber-600' : 'text-slate-500'
                  }`}>
                    {eval_.status === 'completed' && '✓ Completed'}
                    {eval_.status === 'acknowledged' && '✓ Signed'}
                    {eval_.status === 'pending_signature' && '⏳ Pending Signature'}
                    {eval_.status === 'draft' && '○ Draft'}
                    {!['completed', 'acknowledged', 'pending_signature', 'draft'].includes(eval_.status) && '○ Draft'}
                  </p>
                </div>
              </div>

              {/* Duty Ratings Summary */}
              <div className="mt-4 grid grid-cols-5 gap-2">
                {pcwDuties.slice(0, 5).map((duty) => (
                  <div key={duty.id} className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500 truncate" title={duty.description}>Duty {duty.number}</p>
                    <span className={`text-sm font-medium ${getGradeColor(eval_[duty.id])}`}>
                      {eval_[duty.id]}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-5 gap-2">
                {pcwDuties.slice(5, 10).map((duty) => (
                  <div key={duty.id} className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500 truncate" title={duty.description}>Duty {duty.number}</p>
                    <span className={`text-sm font-medium ${getGradeColor(eval_[duty.id])}`}>
                      {eval_[duty.id]}
                    </span>
                  </div>
                ))}
              </div>

              {/* Goals */}
              {(eval_.goal_1 || eval_.goal_2 || eval_.goal_3) && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Goals Set by RN</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {eval_.goal_1 && <li>1. {eval_.goal_1}</li>}
                    {eval_.goal_2 && <li>2. {eval_.goal_2}</li>}
                    {eval_.goal_3 && <li>3. {eval_.goal_3}</li>}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-3 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExportPdf(eval_.id)}
                  className="flex items-center gap-2"
                  data-testid={`export-pdf-btn-${eval_.id}`}
                >
                  <FileText className="w-4 h-4" />
                  Export PDF
                </Button>
                {eval_.status !== 'completed' && eval_.status !== 'pending_signature' && eval_.status !== 'acknowledged' && (
                  <Button
                    size="sm"
                    onClick={() => onCompleteAndSend(eval_.id, eval_.employee_name)}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`complete-send-btn-${eval_.id}`}
                  >
                    <Send className="w-4 h-4" />
                    Complete & Send for Signature
                  </Button>
                )}
                {eval_.status === 'pending_signature' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onResend(eval_.id, eval_.employee_name)}
                      className="flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`resend-btn-${eval_.id}`}
                    >
                      <Send className="w-4 h-4" />
                      Resend
                    </Button>
                    <span className="text-sm text-amber-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Awaiting PCW Signature
                    </span>
                    {eval_.first_viewed_at && (
                      <span className="text-sm text-blue-600 flex items-center gap-1 ml-2" title={`Viewed ${eval_.view_count || 1} time(s). Last viewed: ${new Date(eval_.last_viewed_at || eval_.first_viewed_at).toLocaleString()}`}>
                        <Eye className="w-4 h-4" />
                        Viewed {new Date(eval_.first_viewed_at).toLocaleDateString()}
                        {eval_.view_count > 1 && <span className="text-xs text-slate-500">({eval_.view_count}x)</span>}
                      </span>
                    )}
                    {!eval_.first_viewed_at && eval_.sent_at && (
                      <span className="text-sm text-slate-400 flex items-center gap-1 ml-2">
                        <Eye className="w-4 h-4" />
                        Not viewed yet
                      </span>
                    )}
                  </>
                )}
                {eval_.status === 'acknowledged' && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Signed by PCW
                    {eval_.employee_signature_date && (
                      <span className="text-slate-500 ml-1">
                        on {new Date(eval_.employee_signature_date).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
